import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  ArrowRight, Sparkles, BookOpen, BarChart2, Target, Users, Shield, 
  CheckCircle, PlayCircle, Star, Brain, Layout, Clock, Award, Code, GraduationCap,
  Cpu, Database, Network
} from 'lucide-react';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const TypewriterText = ({ text }) => {
  const [displayText, setDisplayText] = useState('');
  
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      setDisplayText(text.slice(0, index));
      index++;
      if (index > text.length) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, [text]);

  return <span>{displayText}<motion.span animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 0.8 }}>|</motion.span></span>;
};

export default function Home() {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <div className="home-dark-contrast min-h-screen bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] selection:bg-teal-600/30 overflow-hidden font-sans transition-colors duration-300">
      <Navbar />

      {/* Dynamic Bright Animated Background Gradients */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-40">
        <motion.div 
          animate={{ scale: [1, 1.1, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-teal-600/20 blur-[120px]" 
        />
        <motion.div 
          animate={{ scale: [1, 1.2, 1], rotate: [0, -90, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute top-[20%] right-[-10%] w-[40%] h-[50%] rounded-full bg-amber-400/20 blur-[120px]" 
        />
        <motion.div 
          animate={{ x: [0, 50, 0], y: [0, -50, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[-20%] left-[20%] w-[50%] h-[50%] rounded-full bg-amber-400/20 blur-[120px]" 
        />
      </div>

      <main className="relative z-10">
        {/* 1. HERO SECTION (Redesigned & AI Powered) */}
        <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 overflow-hidden">
          {/* AI Generated Background Overlay */}
          <div className="absolute inset-0 z-0 bg-[url('/ai_hero_bg.webp')] bg-cover bg-center opacity-40 mix-blend-screen pointer-events-none" />
          {/* Soft wash — keep readability, stay transparent enough for the AI art to lead */}
          <div className="absolute inset-0 z-0 bg-gradient-to-b from-[var(--color-bg-primary)]/45 via-[var(--color-bg-primary)]/20 to-[var(--color-bg-primary)]/70 pointer-events-none" />
          
          <div className="max-w-7xl mx-auto text-center relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-600/20 border border-teal-400/30 text-sm mb-8 shadow-sm backdrop-blur-md"
              style={{ color: 'var(--text-primary)' }}
            >
              <Sparkles size={16} style={{ color: 'var(--text-accent-cyan)' }} />
              <span className="font-medium tracking-wide">Introducing AI-Driven Curriculums</span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight mb-8 leading-[1.05] text-[var(--color-text-primary)]"
            >
              The Next-Gen Platform for <br className="hidden md:block" />
              <span className="home-gradient-text text-transparent bg-clip-text bg-gradient-to-r from-teal-500 via-teal-400 to-amber-400">
                SaaS & Academy Growth
              </span>
            </motion.h1>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl max-w-2xl mx-auto mb-10 h-20 md:h-auto"
              style={{ color: 'var(--text-secondary)' }}
            >
              Empower your team and your students. Manage complex tech organizations, 
              intricate learning curriculums, and scale operations effortlessly using <br/>
              <strong style={{ color: 'var(--text-accent-cyan)' }}><TypewriterText text="Advanced Artificial Intelligence" /></strong>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/auth/register" className="btn max-sm:w-full sm:min-w-[220px] h-16 px-10 text-lg whitespace-nowrap bg-gradient-to-r from-teal-600 via-teal-500 to-amber-400 text-white font-extrabold rounded-full hover:brightness-110 transition-all duration-200 transform hover:scale-[1.04] active:scale-[0.98] flex items-center justify-center gap-2.5 shadow-[0_10px_40px_-8px_rgba(47,124,123,0.55)] border border-white/15">
                Get Started <ArrowRight size={20} />
              </Link>
              <button className="max-sm:w-full sm:min-w-[220px] h-16 px-10 text-lg whitespace-nowrap bg-white/10 border border-white/25 text-white font-extrabold rounded-full hover:bg-white/15 hover:border-cyan-300/60 transition-all duration-200 transform hover:scale-[1.04] active:scale-[0.98] flex items-center justify-center gap-2.5 shadow-lg relative overflow-hidden group backdrop-blur-md">
                <span className="relative z-10 flex items-center gap-2.5">Watch Demo <PlayCircle size={20} className="text-cyan-300 group-hover:scale-110 transition-transform" /></span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal-600/15 to-amber-400/15 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </motion.div>

            {/* AI Hero Visual Interactive Mockup */}
            <motion.div 
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-24 relative max-w-4xl mx-auto perspective-1000"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-bg-primary)] via-transparent to-transparent z-10 rounded-2xl pointer-events-none" />
              
              <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-card-bg)] shadow-2xl relative p-6 md:p-8 transform rotate-x-12 hover:rotate-x-0 transition-transform duration-700 ease-out backdrop-blur-xl">
                {/* Simulated IDE / AI Interface Header */}
                <div className="flex items-center justify-between mb-8 border-b border-[var(--color-border)] pb-4">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400 opacity-80" />
                    <div className="w-3 h-3 rounded-full bg-amber-400 opacity-80" />
                    <div className="w-3 h-3 rounded-full bg-green-400 opacity-80" />
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold bg-teal-600/10 border border-teal-600/20 px-3 py-1 rounded-full" style={{ color: 'var(--text-accent-blue)' }}>
                    <Brain size={14} className="animate-pulse" /> AI Engine Active
                  </div>
                </div>

                {/* AI Activity Simulation */}
                <div className="grid md:grid-cols-3 gap-6 text-left">
                  <div className="md:col-span-2 space-y-4">
                     <motion.div 
                       initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 1 }}
                       className="p-4 bg-[var(--color-bg-primary)] rounded-xl border border-[var(--color-border)] shadow-sm"
                     >
                       <div className="flex items-center gap-3 mb-2">
                         <div className="w-8 h-8 rounded-lg bg-teal-600/10 flex items-center justify-center text-teal-600"><Users size={16}/></div>
                         <div className="h-4 w-32 bg-[var(--color-border)] rounded animate-pulse" />
                       </div>
                       <div className="h-2 w-full bg-[var(--color-border)] rounded mb-2" />
                       <div className="h-2 w-2/3 bg-[var(--color-border)] rounded" />
                     </motion.div>
                     
                     <motion.div 
                       initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 1.5 }}
                       className="p-4 bg-teal-600/10 rounded-xl border border-teal-600/30 shadow-sm relative overflow-hidden"
                     >
                       <div className="absolute inset-0 bg-gradient-to-r from-transparent via-teal-600/20 to-transparent opacity-50 animate-[translation_2s_infinite]" />
                       <div className="flex items-center gap-3 mb-2 relative z-10">
                         <div className="w-8 h-8 rounded-lg bg-teal-700 flex items-center justify-center text-white"><Sparkles size={16}/></div>
                         <h4 className="font-bold text-[var(--color-text-primary)] text-sm">Generating Learning Path...</h4>
                       </div>
                       <div className="space-y-2 mt-3 relative z-10">
                        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-accent-blue)' }}>
                           <CheckCircle size={12} /> Analyzing student skill gaps
                         </div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-accent-blue)' }}>
                           <CheckCircle size={12} /> Compiling relevant course modules
                         </div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-accent-cyan)' }}>
                           <Clock size={12} className="animate-spin" /> Structuring assessments
                         </div>
                       </div>
                     </motion.div>
                  </div>
                  
                  <div className="bg-[var(--color-bg-primary)] rounded-xl p-5 border border-[var(--color-border)] flex flex-col items-center justify-center relative overflow-hidden shadow-inner">
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 opacity-20"
                      style={{ backgroundImage: 'radial-gradient(circle at center, #2F7C7B 1px, transparent 1px)', backgroundSize: '16px 16px' }}
                    />
                    <div className="relative z-10 w-24 h-24 rounded-full border-4 border-teal-600/20 flex items-center justify-center mb-4 bg-[var(--color-bg-secondary)]">
                      <motion.div 
                        animate={{ scale: [1, 1.1, 1] }} 
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="w-16 h-16 rounded-full bg-gradient-to-tr from-teal-600 to-amber-400 flex items-center justify-center shadow-lg shadow-teal-600/40"
                      >
                        <Network className="text-white" size={28} />
                      </motion.div>
                    </div>
                    <h5 className="font-bold text-[var(--color-text-primary)] text-sm z-10">Neural Mentor</h5>
                    <p className="text-xs text-[var(--color-text-secondary)] text-center mt-1 z-10">Processing <br/> 2.4B parameters</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 2. TRUST SECTION */}
        <section className="py-12 border-y border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-sm font-bold text-[var(--color-text-secondary)] uppercase tracking-widest mb-8">Trusted by innovative companies</p>
            <div className="home-marquee">
              <div className="home-marquee-track">
                {[...Array(2)].flatMap((_, dup) =>
                  ['Stripe', 'Vercel', 'Linear', 'Notion', 'Framer'].map((brand, i) => (
                    <span key={`${dup}-${i}`} aria-hidden={dup === 1} className="text-2xl md:text-3xl font-extrabold tracking-tighter text-[var(--color-text-primary)] opacity-70 hover:opacity-100 transition-opacity">{brand}</span>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 3. FEATURES (Bento Grid) */}
        <section className="py-24 px-6 relative border-b border-[var(--color-border)]">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[var(--color-text-primary)]">A unified operating system.</h2>
              <p className="text-[var(--color-text-primary)] text-lg max-w-xl">Everything you need to run your SaaS backend and power a world-class academy experience.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Feature 1 */}
              <motion.div 
                whileHover={{ y: -6 }}
                className="home-card md:col-span-2 bg-[var(--color-card-bg)] border border-[var(--color-border)] shadow-sm rounded-[2rem] p-8 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-600/10 rounded-full blur-3xl group-hover:bg-teal-600/20 transition-colors pointer-events-none" />
                <Layout className="text-teal-600 mb-6" size={32} />
                <h3 className="text-2xl font-bold mb-3 text-[var(--color-text-primary)]">Smart Dashboard</h3>
                <p className="text-[var(--color-text-primary)]">View real-time analytics, user growth, and content engagement in one beautiful control center.</p>
                <div className="mt-8 h-40 bg-[var(--color-bg-primary)] rounded-xl border border-[var(--color-border)] p-4 flex gap-4">
                   <div className="w-1/3 bg-[var(--color-card-bg)] rounded-lg shadow-sm border border-[var(--color-border)]" />
                   <div className="w-2/3 flex flex-col gap-3">
                     <div className="h-10 bg-[var(--color-card-bg)] rounded-lg shadow-sm border border-[var(--color-border)] opacity-80" />
                     <div className="flex-1 bg-[var(--color-card-bg)] rounded-lg shadow-sm border border-[var(--color-border)] flex items-end p-3 gap-2">
                        {[40, 70, 45, 90, 65, 80].map((h, i) => (
                          <div key={i} className="flex-1 bg-teal-600/40 rounded-t-sm hover:bg-teal-600/60 transition-colors" style={{ height: `${h}%` }} />
                        ))}
                     </div>
                   </div>
                </div>
              </motion.div>

              {/* Feature 2 */}
              <motion.div 
                whileHover={{ y: -6 }}
                className="home-card bg-[var(--color-card-bg)] border border-[var(--color-border)] shadow-sm rounded-[2rem] p-8 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full blur-2xl group-hover:bg-amber-400/20 transition-colors pointer-events-none" />
                <Brain className="text-amber-500 mb-6" size={32} />
                <h3 className="text-2xl font-bold mb-3 text-[var(--color-text-primary)]">AI Recommendations</h3>
                <p className="text-[var(--color-text-primary)] mb-6">Personalized learning paths generated automatically based on skill gaps.</p>
                <div className="space-y-4 pt-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-3 w-full bg-[var(--color-bg-primary)] rounded-full overflow-hidden border border-[var(--color-border)]">
                      <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: `${Math.random() * 40 + 40}%` }}
                        transition={{ duration: 1, delay: 0.2 * i }}
                        className="h-full bg-gradient-to-r from-amber-400 to-teal-600 rounded-full" 
                      />
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Feature 4 */}
              <motion.div 
                whileHover={{ y: -6 }}
                className="home-card md:col-span-3 bg-gradient-to-br from-[#0F1F23] to-gray-900 border border-teal-600/30 rounded-[2rem] p-8 flex flex-col md:flex-row items-center gap-8 shadow-xl relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-teal-600/20 via-transparent to-transparent pointer-events-none" />
                
                <div className="flex-1 relative z-10">
                  <BookOpen className="text-cyan-300 mb-6" size={32} />
                  <h3 className="text-2xl font-bold mb-3 text-cyan-300">Course Management</h3>
                  <p className="text-teal-50">Build incredible coding curriculums or marketing courses with an intuitive drag-and-drop builder.</p>
                </div>
                <div className="flex-1 w-full bg-[#0a0a0a]/80 backdrop-blur-md rounded-2xl border border-white/10 p-5 space-y-4 shadow-inner relative z-10">
                  <div className="flex items-center gap-4 bg-white/5 p-3 rounded-xl border border-white/10 text-cyan-200 hover:bg-white/10 transition-colors"><Code size={18} className="text-teal-300"/> Interactive Exercises</div>
                  <div className="flex items-center gap-4 bg-white/5 p-3 rounded-xl border border-white/10 text-cyan-200 hover:bg-white/10 transition-colors"><PlayCircle size={18} className="text-amber-300"/> Video Streaming</div>
                  <div className="flex items-center gap-4 bg-white/5 p-3 rounded-xl border border-white/10 text-cyan-200 hover:bg-white/10 transition-colors"><CheckCircle size={18} className="text-emerald-300"/> Automatic Grading</div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 4. HOW IT WORKS */}
        <section className="py-24 bg-[var(--color-bg-primary)] border-b border-[var(--color-border)]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 text-[var(--color-text-primary)]">Seamless execution.</h2>
              <p className="text-[var(--color-text-primary)] text-lg">Designed for speed, scaling from solo creators to massive organizations.</p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Users, title: "1. Onboard", desc: "Easily sync your organization or invite students via magic links." },
                { icon: Layout, title: "2. Construct", desc: "Publish rich courses and construct complex onboarding flows." },
                { icon: Brain, title: "3. Learn", desc: "Users experience an AI-tailored environment focused on engagement." },
                { icon: BarChart2, title: "4. Measure", desc: "Analyze drop-offs, track success, and issue certificates automatically." }
              ].map((step, idx) => (
                <motion.div 
                  key={idx}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  className="relative group text-center md:text-left"
                >
                  <div className="w-16 h-16 bg-[var(--color-card-bg)] border border-[var(--color-border)] shadow-sm rounded-[1.25rem] flex items-center justify-center mb-6 mx-auto md:mx-0 group-hover:border-teal-500/60 group-hover:bg-teal-600/10 group-hover:shadow-[0_12px_30px_-10px_rgba(47,124,123,0.45)] group-hover:-translate-y-1 group-hover:rounded-full transition-all duration-500" style={{ transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)' }}>
                    <step.icon size={26} className="text-teal-500 group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <h4 className="text-xl font-bold mb-2 text-[var(--color-text-primary)]">{step.title}</h4>
                  <p className="text-[var(--color-text-primary)] text-sm leading-relaxed">{step.desc}</p>
                  {idx !== 3 && <div className="hidden md:block absolute top-7 left-20 right-0 h-px bg-[var(--color-border)]" />}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. BENEFITS / VALUE */}
        <section className="py-24 px-6 border-b border-[var(--color-border)] bg-[var(--color-card-bg)]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-12 text-[var(--color-text-primary)]">Built for speed and scale.</h2>
            <div className="grid sm:grid-cols-2 gap-8">
              {[
                { metric: "10x", label: "Faster Onboarding", desc: "Automate approvals and content assignments." },
                { metric: "99.9%", label: "Uptime SLA", desc: "Enterprise reliability for mission-critical training." },
                { metric: "24/7", label: "AI Assistance", desc: "Instant answers for students via the semantic knowledge base." },
                { metric: "0", label: "Coding Required", desc: "No-code editor makes course creation a breeze." }
              ].map((val, idx) => (
                <div key={idx} className="home-card p-8 rounded-[2rem] bg-[var(--color-bg-primary)] border border-[var(--color-border)]">
                  <div className="text-4xl font-extrabold text-emerald-500 mb-2">{val.metric}</div>
                  <div className="text-xl font-bold mb-2 text-emerald-500">{val.label}</div>
                  <div className="text-[var(--color-text-primary)] text-sm">{val.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 7. TESTIMONIALS */}
        <section className="py-24 px-6 relative bg-[var(--color-bg-primary)]">
          <div className="max-w-7xl mx-auto">
             <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-[var(--color-text-primary)]">Loved by educators and engineers.</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { quote: "Learnly revolutionized how we train our internal engineering teams. The AI features are purely magical.", author: "Sarah Jenkins", role: "VP Engineering, TechFlow", color: "bg-teal-700" },
                { quote: "Our course completion rate jumped by 80% since migrating to this platform. The interface is simply gorgeous.", author: "David Chen", role: "Founder, CodeMastery", color: "bg-amber-500" },
                { quote: "The best administrative tools I've ever used. Completely automated our compliance and reporting.", author: "Elena Rodriguez", role: "Director of HR, GlobalCorp", color: "bg-[#2FA7A1]" }
              ].map((test, idx) => (
                <motion.div 
                   key={idx}
                   whileHover={{ y: -6 }}
                   className="home-card p-8 rounded-[2rem] bg-[var(--color-card-bg)] border border-[var(--color-border)] shadow-sm flex flex-col justify-between"
                >
                  <div>
                    <div className="flex gap-1 mb-6">
                      {[1,2,3,4,5].map(i => <Star key={i} size={16} className="text-amber-400 fill-amber-400 opacity-80" />)}
                    </div>
                    <p className="text-[var(--color-text-primary)] text-lg leading-relaxed mb-8 italic">"{test.quote}"</p>
                  </div>
                  <div className="flex items-center gap-4 border-t border-[var(--color-border)] pt-6">
                    <div className={`w-12 h-12 rounded-full ${test.color} flex items-center justify-center font-bold text-white shadow-md`}>
                      {test.author.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-bold text-[var(--color-text-primary)] text-sm">{test.author}</h4>
                      <p className="text-xs text-[var(--color-text-secondary)] font-medium">{test.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. FINAL CTA */}
        <section className="py-24 px-6 relative bg-[var(--color-card-bg)] border-t border-[var(--color-border)]">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-teal-700 via-teal-600 to-[#0F1F23] p-12 md:p-20 text-center shadow-2xl shadow-teal-600/30 border border-white/10">
              {/* Decorative aurora glows */}
              <div className="home-aurora absolute -top-1/3 -left-1/4 w-2/3 h-2/3 rounded-full bg-cyan-400/25 blur-[100px] pointer-events-none" />
              <div className="home-aurora absolute -bottom-1/3 -right-1/4 w-2/3 h-2/3 rounded-full bg-amber-400/25 blur-[100px] pointer-events-none" style={{ animationDelay: '-7s' }} />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent pointer-events-none" />
              
              <div className="relative z-10">
                <h2 className="text-4xl md:text-6xl font-extrabold mb-6 text-white tracking-tight">Start building your platform today.</h2>
                <p className="text-teal-50 text-lg md:text-xl max-w-2xl mx-auto mb-10">
                  Join hundreds of organizations scaling their knowledge transfer and educational products with Learnly.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link to="/auth/register" className="h-16 px-10 text-lg bg-white text-teal-800 font-extrabold rounded-full hover:bg-teal-50 transition-all transform hover:scale-[1.04] active:scale-[0.98] flex items-center justify-center gap-2.5 shadow-xl">
                    Create Free Account <ArrowRight size={20} />
                  </Link>
                  <Link to="/contact" className="h-16 px-10 text-lg bg-black/25 border border-white/30 text-white font-extrabold rounded-full hover:bg-black/40 transition-all transform hover:scale-[1.04] active:scale-[0.98] flex items-center justify-center backdrop-blur-sm">
                    Talk to Sales
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
